import React, { useCallback, useEffect, useState } from 'react'
import { fetchDeviceData } from './deviceSlice';
import { useDispatch, useSelector } from 'react-redux';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { Status } from '../../common/constants';
import { useSpinner } from '../../customHooks';
import { useTableRowsSelection } from './useTableRowsSelection';
import { IGroupRow, IDeviceRow, IDeviceApiState, IAssignDevice, IAssignDevicesResponseState } from '../../common/types/devicesTypes';
import { selector as groupApi } from './groupSlice';
import { IHierarchyNode } from './util';
import { actions as modalActions } from '../../components/createEditGroupModal/createEditGroupModalSlice';

const useDeviceManagement = () => {

  const dispatch: ThunkDispatch<IDeviceApiState, undefined, Action> = useDispatch();
  const dispatchDevices: ThunkDispatch<IAssignDevicesResponseState, undefined, Action> =
    useDispatch();
  const deviceStatus: string = useSelector((state: IDeviceApiState) => state.deviceApi.status);
  const groupsHierarchy: IHierarchyNode[] = useSelector(groupApi).groupsHierarchy;
  const groupsStatus: string = useSelector(groupApi).status;
  const assignDevicesResponse = useSelector((state: IAssignDevicesResponseState) => state.assignDevices);
  const status: string = assignDevicesResponse?.status === Status.PENDING ? assignDevicesResponse.status : (groupsStatus === Status.PENDING ? groupsStatus : deviceStatus);
  useSpinner(status);

  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isEmptyGroupShown, setIsEmptyGroupShown] = useState<boolean>(true);
  const [isSearching, setIsSearching] = useState<boolean>(false)
  const [shouldResetSelection, setShouldResetSelection] = useState(false);
  const [showAssignModel, setShowAssignModel] = useState<boolean>(false);

  const handleClose = () => {
    setShowAssignModel(false);
  }

  useEffect(() => {
    dispatch(fetchDeviceData(''));
    setIsEmptyGroupShown(true)
  }, [])

  const handleOpen = () => setShowAssignModel(true);

  const { selectedRows, setSelectedRows, onRowSelectionChange } = useTableRowsSelection<IGroupRow | IDeviceRow>();

  const [deviceArray, setDeviceArray] = useState<IAssignDevice[]>([])
  useEffect(() => {
    const newDeviceArray = selectedRows.reduce((accumulator, row) => {
      if (row && 'deviceId' in row) {
        const device: IAssignDevice = {
          deviceId: row.deviceId,
          parentHierarchy: row.parentHierarchy,
          deviceDisplayName: row.deviceDisplayName,
        }
        return [...accumulator, device];
      }
      return accumulator;
    }, []);
    const filtereDeviceArray: IAssignDevice[] = newDeviceArray?.reduce((accumulatedDevices, currentDevice) => {
      if (accumulatedDevices.length === 0) return [currentDevice];
      const exists = accumulatedDevices.some((accumulatedDevice: IAssignDevice) => accumulatedDevice.deviceId === currentDevice.deviceId);
      if (!exists) return [...accumulatedDevices, currentDevice];
      return accumulatedDevices;
    }, [])
    setDeviceArray(filtereDeviceArray);
  }, [selectedRows]);

  useEffect(() => {
    setShouldResetSelection(true);
    setSelectedRows([]);
    setDeviceArray([]);
  },[groupsHierarchy, deviceStatus]);
  
  const acknowledgeReset = () => setShouldResetSelection(false);
  React.useEffect(() => {
    if (assignDevicesResponse.status == Status.SUCCESS) {
      dispatchDevices(fetchDeviceData(""));
    }
    else
    if (assignDevicesResponse.status == Status.FAILED) {
      setShouldResetSelection(true);
    }
  }, [assignDevicesResponse.status]);

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setIsSearching(false);
    if (event.target.value === '') {
      dispatch(fetchDeviceData(''));
      setIsEmptyGroupShown(true);
      setIsSearching(true)
    }
  }, []);

  const handleSearchSubmit = useCallback((e) => {
    e.preventDefault();
    if (searchTerm !== "") {
      dispatch(fetchDeviceData(searchTerm));
      setIsEmptyGroupShown(false)
      setIsSearching(true)
    }
  }, [searchTerm, dispatch]);

  const showModal = (): void => {
    dispatch(modalActions.showModal());
  }

  return {
    handleSearchSubmit,
    searchTerm,
    handleSearchChange,
    groupsHierarchy,
    deviceArray,
    selectedRows,
    onRowSelectionChange,
    isEmptyGroupShown,
    setIsEmptyGroupShown,
    setSearchTerm,
    isSearching,
    setDeviceArray,
    setSelectedRows,
    showModal,
    showAssignModel,
    handleClose,
    handleOpen,
    shouldResetSelection,
    acknowledgeReset
  }

};

export default useDeviceManagement
